import React from 'react';

export default function Stylophone({ handlePageChange }) {
  return (
    <div className='container' style={{ marginTop: '2rem' }}>
      <div className='row'>
        <div className='col s12'>
          <h1 className='header center-on-small-only'>
            Stylophone: <br /> Analog / Digital Synthesizer
          </h1>
          <div className='divider'></div>
        </div>
      </div>

      {/* Hero Section */}
      <div className='row section'>
        <div className='col s12 m6'>
          <a
            href='./assets/stylo_hero.png'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/stylo_hero.png'
              className='responsive-img materialboxed z-depth-2'
              alt='Finished Stylophone'
              style={{ cursor: 'pointer' }}
            />
          </a>
        </div>
        <div className='col s12 m6'>
          <h3>Overview</h3>
          <p className='flow-text'>
            Combining both CAD and hands-on skills, a team of three of us built
            a functioning synthesizer from scratch, including a milled PCB and
            3D-printed enclosure.
          </p>
        </div>
      </div>

      {/* Specs and Skills Section */}
      <div className='row section'>
        <div className='divider'></div>
        <div className='col s12 m6'>
          <h4>Project Specs</h4>
          <ul className='browser-default' style={{ fontSize: '1.2rem' }}>
            <li>22-note synthesier with 9V battery power</li>
            <li>IC chips: LM555 timer and LM386 amp</li>
            <li>Speaker and headphone output with volume control</li>
            <li>Custom milled PCB</li>
            <li>3D-printed custom enclosure</li>
          </ul>
        </div>
        <div className='col s12 m6'>
          <h4>Skills Learned</h4>
          <ul className='browser-default' style={{ fontSize: '1.2rem' }}>
            <li>Circuit Design & Analysis</li>
            <li>PCB Routing & Milling Workflow</li>
            <li>CAD Modeling in Fusion</li>
            <li>Hardware Troubleshooting</li>
            <li>Component Soldering & Assembly</li>
            <li>Project & Team Management</li>
          </ul>
        </div>
        <div className='col s12'>
          <div className='divider'></div>
        </div>
      </div>

      {/* Technical Deep Dive */}
      <div className='row section'>
        <div className='col s12 m7'>
          <h4>Circuit Design</h4>
          <p>
            There are many ways to wire up a stylophone with a 555 timing chip.
            Ours takes inspiration from{' '}
            <a
              href='https://www.instructables.com/A-Stylophone/'
              target='_blank'
            >
              this design
            </a>{' '}
            – using the 555's comparator function to produce a squarewave whose
            frequency is manipulated through a bank of resistors.
          </p>
          {/* <ul className='collection with-header'>
            <li className='collection-header'>
              <h5>Key Components</h5>
            </li>
            <li className='collection-item'>
              555 Precision Timer (The Oscillator)
            </li>
            <li className='collection-item'>
              LM386 Audio Amplifier (Output Gain)
            </li>
            <li className='collection-item'>
              Precision Resistor Ladder (Keyboard Logic)
            </li>
          </ul> */}
        </div>

        {/* Simplified Circuit */}
        <div className='col s12 m5'>
          <img
            src='./assets/555simple.png'
            className='responsive-img'
            alt='Stylophone Schematic'
          />
          {/* <a
            href='./assets/555simple.png'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/555simple.png'
              className='responsive-img'
              alt='Stylophone Schematic'
            />
          </a> */}
          <p className='caption center'>Simplified circuit.</p>
        </div>
      </div>

      {/* Full Width Circuit Schematic */}
      <div className='row section'>
        <div className='col s12'>
          <a
            href='./assets/schematic.pdf'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/schematic.png'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='Full Circuit Schematic'
            />
          </a>
          <p className='caption center'>
            Full circuit schematic done in Fusion.
          </p>
        </div>
      </div>

      {/* Build Process */}
      <div className='row section'>
        <div className='col s12 m7'>
          <h4>Build Process</h4>
          <p>
            After a quick and scrappy proof of concept using breadboards and
            tinfoil keys, the entire circuit was drafted in Fusion, which made
            it easy to both convert our design into a Gerber file for PCB
            milling, and to model a custom enclosure that would fit our board
            perfectly.
          </p>
        </div>

        {/* Breadboard */}
        <div className='col s12 m5'>
          <a
            href='./assets/breadboard.jpg'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/breadboard.jpg'
              className='responsive-img z-depth-1'
              alt='Breadboard prototype'
            />
          </a>
          <p className='caption center'>Breadboard prototype.</p>
        </div>
      </div>

      {/* PCB images */}
      <div
        className='row section'
        style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
      >
        {/* Left Image Column */}
        <div className='col s12 m6'>
          <a
            href='./assets/pcb_translucent.png'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/pcb_translucent.png'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='Description of first image'
            />
          </a>
        </div>

        {/* Right Image Column */}
        <div className='col s12 m6'>
          <a
            href='./assets/pcb_iso.png'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/pcb_iso.png'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='Description of second image'
            />
          </a>
        </div>

        {/* Caption */}
        <div className='col s12'>
          <p className='caption center'>PCB routing and modeling.</p>
        </div>
      </div>

      {/* Enclosure Images*/}
      <div
        className='row section'
        style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
      >
        {/* Photo 1 */}
        <div className='col s12 m4'>
          <a
            href='./assets/enclosure_base.png'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/enclosure_base.png'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='enclosure base'
            />
          </a>
        </div>

        {/* Photo 2 */}
        <div className='col s12 m4'>
          <a
            href='./assets/enclosure_pcb.png'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/enclosure_pcb.png'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='enclosure pcb fit'
            />
          </a>
        </div>

        {/* Photo 3 */}
        <div className='col s12 m4'>
          <a
            href='./assets/stylo_render.png'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/stylo_render.png'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='Final stylophone model render'
            />
          </a>
        </div>
        {/* Alt image if not using render */}
        {/* <div className='col s12 m4'>
          <a
            href='./assets/enclosure_pcb.png'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/enclosure_pcb.png'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='enclosure pcb fit'
            />
          </a>
        </div> */}

        {/* Shared Caption PCB*/}
        <div className='col s12'>
          <p className='caption center'>Enclosure modeling and final render.</p>
        </div>
      </div>

      {/* Fabrication */}
      <div
        className='row section'
        style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
      >
        {/* Left Image Column */}
        <div className='col s12 m6'>
          <a
            href='./assets/pcb_mill.jpg'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/pcb_mill.jpg'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='Milling of the PCB'
            />
          </a>
        </div>

        {/* Right Image Column */}
        <div className='col s12 m6'>
          <a
            href='./assets/pcb_assembly.jpg'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/pcb_assembly.jpg'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='PCB assembly'
            />
          </a>
        </div>

        {/* Caption */}
        <div className='col s12'>
          <p className='caption center'>PCB milling and component assembly.</p>
        </div>
      </div>

      {/* Full Width Render
      <div className='row section'>
        <div className='col s12'>
          <a
            href='./assets/stylo_render.png'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/stylo_render.png'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='CAD render of full stylophone assembly'
            />
          </a>
          <p className='caption center'>Render of modeled assembly.</p>
        </div>
      </div> */}

      {/* Final result */}
      <div className='row section'>
        <div className='col s12 m7'>
          <h4>Final Result</h4>
          <p>
            After some initial but not unexpected woes, our diligent
            troubleshooting yielded a working circuit well within the project
            timeline. I couldn't be more pleased with the result both from a
            form and function point of view.
          </p>
        </div>

        {/* Breadboard */}
        <div className='col s12 m5'>
          <a
            href='./assets/stylo_portrait.jpg'
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='./assets/stylo_portrait.jpg'
              className='responsive-img z-depth-1'
              style={{ width: '100%', cursor: 'pointer' }}
              alt='Final stylophone'
            />
          </a>
          {/* <p className='caption center'>Breadboard prototype.</p> */}
        </div>
      </div>
    </div>
  );
}
